import { useCallback, useState } from "react";
import { type LayoutChangeEvent } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import type { WorklistCounts } from "../utils/worklist";

interface WorklistHeaderProps {
  counts: WorklistCounts;
}

/**
 * Tenant-level scenario metrics shown at the very top of the worklist. These are
 * computed client-side from the already-loaded scenario list (config-redesign
 * design doc §0/§4) — there is deliberately no separate aggregate endpoint. The
 * "Missing" figure is what the operator is here to drive to zero, so it leads and
 * carries the warning colour.
 */
export function WorklistHeader({ counts }: WorklistHeaderProps) {
  const { theme } = useUnistyles();

  // Responsive reflow, mirroring the lat/lng/direction row on the configure
  // page. We measure the row's own width (not the window) because it sits inside
  // WebLayout beside a collapsible sidebar. As space shrinks the cards wrap in
  // DOM order: Total (last) drops to its own line first, then Configured, ending
  // in a fully-stacked (1,1,1) layout. "Missing" always leads.
  const [rowWidth, setRowWidth] = useState(0);
  const onRowLayout = useCallback((e: LayoutChangeEvent) => {
    setRowWidth(e.nativeEvent.layout.width);
  }, []);
  // 0 = not yet measured → assume wide (avoids a stacked flash on first paint).
  // Three metric cards need ~360px to sit comfortably; below that Total wraps to
  // its own full-width line, and below ~240px Configured wraps too.
  const stackConfigured = rowWidth > 0 && rowWidth < 240;
  const stackTotal = rowWidth > 0 && rowWidth < 360;

  return (
    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: theme.spacing.xl }} onLayout={onRowLayout}>
      <Metric label="Missing" value={counts.missing} color={theme.colors.status.warning} />
      <Metric
        label="Configured"
        value={counts.configured}
        color={theme.colors.status.success}
        fullWidth={stackConfigured}
      />
      <Metric label="Total" value={counts.total} color={theme.colors.text} fullWidth={stackTotal} />
    </View>
  );
}

interface MetricProps {
  label: string;
  value: number;
  color: string;
  fullWidth?: boolean;
}

function Metric({ label, value, color, fullWidth = false }: MetricProps) {
  const { theme } = useUnistyles();
  return (
    <View
      style={{
        flexGrow: 1,
        flexShrink: 1,
        // "100%" forces this card onto its own line; otherwise it shares the row
        // and grows to fill available space (min 120px keeps the number legible).
        flexBasis: fullWidth ? "100%" : 120,
        minWidth: 0,
        paddingVertical: theme.spacing.xl,
        paddingHorizontal: theme.spacing["3xl"],
        borderRadius: theme.spacing.xl,
        borderWidth: 1,
        borderColor: theme.colors.borderCard,
        backgroundColor: theme.colors.surfaceCard,
        gap: theme.spacing.xs,
      }}
    >
      <Text textStyle="3xl" fontWeight="700" style={{ color }}>
        {value}
      </Text>
      <Text textStyle="xs/medium" color="textSecondary">
        {label}
      </Text>
    </View>
  );
}
